<template>
  <div
    :class="rootClasses"
    class="w-checklist-item"
    :data-testid="sectionTag"
    :aria-current="current ? 'true' : undefined"
    data-w-container-ignore-select
    @click="handleClick"
  >
    <template v-if="appearance === 'default'">
      <span v-if="$slots.prepend" class="w-checklist-item__prepend">
        <slot name="prepend" />
      </span>
      <WCheckbox
        v-model="booleanModel"
        :disabled="disabled"
        :section-tag="sectionTag ? `${sectionTag}-checkbox` : undefined"
        class="w-checklist-item__checkbox"
        @click.stop
      />
      <div class="w-checklist-item__text">
        <span class="w-checklist-item__title label-semibold-small">{{ title }}</span>
        <span v-if="subtitle" class="w-checklist-item__subtitle label-regular-small">{{ subtitle }}</span>
      </div>
      <span v-if="color" class="w-checklist-item__color-dot" :style="{ backgroundColor: color }" />
      <span v-if="$slots.append" class="w-checklist-item__append">
        <slot name="append" />
      </span>
    </template>

    <template v-else>
      <span v-if="$slots.prepend" class="w-checklist-item__prepend">
        <slot name="prepend" />
      </span>
      <WSelectionControl
        v-model="model"
        class="w-checklist-item__selection-control"
        :disabled="disabled"
        :indeterminate="indeterminate"
        :input-interactive="isInputInteractive"
        :interactive="false"
        :name="name"
        :section-tag="sectionTag ? `${sectionTag}-selection-control` : undefined"
        :selected="selected"
        :size="selectionControlSize"
        :subtitle="selectionSubtitle"
        :title="title"
        :value="value"
        :variant="control"
        @select="handleSelectionControlSelect"
      />
      <span v-if="color" class="w-checklist-item__color-dot" :style="{ backgroundColor: color }" />
      <span v-if="$slots.append" class="w-checklist-item__append">
        <slot name="append" />
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { tokens } from '@/plugins/vuetify'
import WCheckbox from './WCheckbox.vue'
import WSelectionControl from './WSelectionControl.vue'
import { getNextSelectionControlModel, isSelectionControlSelected } from './selectionControlModel'
import type {
  WSelectionControlModelValue,
  WSelectionControlSize,
  WSelectionControlValue
} from './WSelectionControl.types'

export type WChecklistItemControl = 'checkbox' | 'radio' | 'text'
export type WChecklistItemVariant = 'single-line' | 'two-line'
export type WChecklistItemAppearance = 'default' | 'list-item'
export type WChecklistItemSelectionMode = 'model' | 'delegated'

export interface WChecklistItemProps {
  title: string
  subtitle?: string
  color?: string
  disabled?: boolean
  /** Displays the list-item checkbox in its mixed state to indicate partial selection. */
  indeterminate?: boolean
  sectionTag?: string
  appearance?: WChecklistItemAppearance
  control?: WChecklistItemControl
  variant?: WChecklistItemVariant
  selected?: boolean
  /**
   * The row currently open in a master–detail list. When set, the row's highlight follows it
   * instead of the control's checked state, so a checked row still hovers like any other and the
   * open row highlights without being checked. The control keeps following the model.
   */
  current?: boolean
  selectionMode?: WChecklistItemSelectionMode
  name?: string
  value?: WSelectionControlValue
}

const props = withDefaults(defineProps<WChecklistItemProps>(), {
  subtitle: undefined,
  color: undefined,
  disabled: false,
  indeterminate: false,
  sectionTag: 'unspecified',
  appearance: 'default',
  control: 'checkbox',
  variant: undefined,
  selected: undefined,
  current: undefined,
  selectionMode: 'model',
  name: undefined,
  value: undefined
})

const emit = defineEmits<{
  select: [value: WSelectionControlValue | undefined]
}>()

const model = defineModel<WSelectionControlModelValue>({ default: false })

const booleanModel = computed({
  get: () => Boolean(model.value),
  set: (value: boolean) => {
    model.value = value
  }
})

const listVariant = computed<WChecklistItemVariant>(
  () => props.variant ?? (props.subtitle ? 'two-line' : 'single-line')
)

const selectionSubtitle = computed(() => (listVariant.value === 'two-line' ? props.subtitle : undefined))

const selectionControlSize: WSelectionControlSize = 'default'

const isDelegated = computed(() => props.selectionMode === 'delegated')
const isInputInteractive = computed(() => !isDelegated.value)

const isSelected = computed(() => isSelectionControlSelected(model.value, props.value, props.selected))
const isHighlighted = computed(() => props.current ?? isSelected.value)

const rootClasses = computed(() => ({
  'w-checklist-item--disabled': props.disabled,
  'w-checklist-item--list-item': props.appearance === 'list-item',
  'w-checklist-item--single-line': props.appearance === 'list-item' && listVariant.value === 'single-line',
  'w-checklist-item--two-line': props.appearance === 'list-item' && listVariant.value === 'two-line',
  'w-checklist-item--selected': props.appearance === 'list-item' && isHighlighted.value
}))

function handleClick() {
  if (props.disabled) {
    return
  }

  if (props.appearance === 'default') {
    model.value = !model.value
    return
  }

  if (!isDelegated.value) {
    model.value = getNextSelectionControlModel(props.control, model.value, props.value, isSelected.value)
  }

  emit('select', props.value)
}

function handleSelectionControlSelect(value: WSelectionControlValue | undefined) {
  if (isDelegated.value) {
    return
  }

  emit('select', value)
}
</script>

<style scoped lang="scss">
.w-checklist-item {
  display: flex;
  align-items: center;
  gap: v-bind('tokens.spacing.gap.icon');
  padding: v-bind('tokens.spacing.inset.container.extraTight');
  border-radius: v-bind('tokens.borderRadius.layer.small');
  background-color: v-bind('tokens.color.background.surface.default');
  cursor: pointer;
}

// The hover, pressed and selected tokens are translucent blue tints. They are composited over the
// row's own surface instead of replacing it, so a row reads the same tint on any parent: on a grey
// panel a bare tint would let the grey show through and turn the blue grey.
.w-checklist-item:hover:not(.w-checklist-item--disabled, .w-checklist-item--selected) {
  background:
    linear-gradient(v-bind('tokens.color.background.surface.hover'), v-bind('tokens.color.background.surface.hover')),
    v-bind('tokens.color.background.surface.default');
}

.w-checklist-item:active:not(.w-checklist-item--disabled) {
  background:
    linear-gradient(v-bind('tokens.color.background.surface.pressed'), v-bind('tokens.color.background.surface.pressed')),
    v-bind('tokens.color.background.surface.default');
}

.w-checklist-item--disabled {
  cursor: default;
}

.w-checklist-item__checkbox {
  flex-shrink: 0;
}

.w-checklist-item__prepend,
.w-checklist-item__append {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 0;
  color: v-bind('tokens.color.content.icons.primary');
}

.w-checklist-item__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.w-checklist-item__title {
  @include tag(label-semibold-small);

  color: v-bind('tokens.color.content.text.primary');
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.w-checklist-item__subtitle {
  @include tag(label-regular-small);

  color: v-bind('tokens.color.content.text.muted');
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.w-checklist-item__color-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.w-checklist-item--list-item {
  gap: v-bind('tokens.spacing.gap.small');
  width: 100%;
  min-width: 0;
  padding: 0 v-bind('tokens.spacing.inset.control.horizontal.default');
  border-radius: 0;
}

.w-checklist-item--single-line {
  height: 40px;
}

.w-checklist-item--two-line {
  height: 54px;
}

.w-checklist-item--list-item.w-checklist-item--selected {
  background:
    linear-gradient(v-bind('tokens.color.background.surface.pressed'), v-bind('tokens.color.background.surface.pressed')),
    v-bind('tokens.color.background.surface.default');
}

.w-checklist-item__selection-control {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
